import { FunctionComponent, useRef, useState } from 'react'
import { Box, Avatar, Typography, Link, makeStyles } from '@material-ui/core'
import { Content } from 'common/types'
import { ElasticSlider } from 'components/core/slider/ElasticSlider'
import ReactWebMediaPlayer from 'react-web-media-player'
import moment from 'moment'
import styled from 'styled-components/macro'
//@ts-ignore
import ReactTextFormat from 'react-text-format'
import { htmlToReactParser } from 'common/HTMLToReactParser'
import { addLinks } from 'common/utils/addHashtagLinks'
import { PostCardImage } from './PostCardImage'
import { PostCardVideo } from './PostCardVideo'

interface SharedPostProps {
  post: Content
  isDevice: boolean
  theme: any
  history: any
  postEdited: boolean
  userId: string
  parseContentBody: Function
  displayShowMore: Function
  profilePosts?: boolean
}

const StyledSharedPostDescription = styled(Typography)`
  display: inline-block;
  max-width: inherit;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  height: 2.9em;
  line-height: 1.2em;
  white-space: pre-wrap;

  a {
    color: ${props => props.theme.colors.primary};
  }
`

const StyledSharedPostTitle = styled(Box)`
  a {
    color: ${props => props.theme.colors.primary};
  }
`

const StyledSharedShowMoreDescription = styled(Typography)`
  display: inline-block;
  max-width: inherit;
  max-height: inherit;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  line-height: 1.2em;
  white-space: pre-wrap;

  a {
    color: ${props => props.theme.colors.primary};
  }
`

export const SharedPostCard: FunctionComponent<SharedPostProps> = ({
  post,
  isDevice,
  theme,
  parseContentBody,
  history,
  userId,
  displayShowMore,
  profilePosts
}) => {
  const [showMore, setShowMore] = useState(false)
  const videoRef = useRef<HTMLDivElement | null>(null)
  const descriptionRefSharedPost = useRef<HTMLElement>(null)

  const useStyles = makeStyles({
    clickable: {
      color: theme.colors.primary
    }
  })

  const customLinkDecorator = (decoratedHref: string, decoratedText: string, key: number) => (
    <a href={decoratedHref} key={key} target="_blank" rel="noopener" className={classes.clickable}>
      {decoratedText}
    </a>
  )
  const classes = useStyles()

  const getSharedPostDetails = (post: Content, index: number) => {
    return post[index]
  }
  const sharedPostDetails = getSharedPostDetails(post, 1)
  let postUrls

  const getName = (): string => {
    if (sharedPostDetails?.originalPosterCompanyName) {
      return sharedPostDetails.originalPosterCompanyName
    } else {
      return `${sharedPostDetails?.originalPosterFirstName} ${sharedPostDetails?.originalPosterLastName}`
    }
  }

  const sharePostCreatorLink = () => {
    if (sharedPostDetails?.partnerId) {
      history.push(`/company/${sharedPostDetails?.partnerId}`)
    } else {
      userId === sharedPostDetails?.userId
        ? history.push(`/profile/`)
        : history.push(`/profile/${sharedPostDetails?.userId}`)
    }
  }

  return (
    <Box
      bgcolor={profilePosts ? theme.colors.supplementary : theme.colors.bgPrimary}
      borderRadius="0.7em"
      width="100%"
    >
      <Box>
        <Box display="flex" justifyContent="space-between" marginBottom="0.25em">
          <Box display="flex" flexDirection={isDevice ? 'column' : 'row'} gridGap="1em" p="0.5em">
            <Box
              marginBottom={isDevice ? '0.8em' : 0}
              style={{ cursor: 'pointer' }}
              onClick={() => {
                sharePostCreatorLink()
              }}
            >
              <Avatar
                src={sharedPostDetails?.originalPosterImage}
                alt="member with a profile"
                style={{ width: '2em', height: '2em' }}
              />
            </Box>
            <Box>
              <Typography component="span" variant="body2" align="left">
                <Box
                  fontWeight="bold"
                  color={theme.colors.accent1}
                  style={{ cursor: 'pointer' }}
                  onClick={() => {
                    sharePostCreatorLink()
                  }}
                >
                  {getName()}
                </Box>
              </Typography>
              <Typography gutterBottom variant="caption" component="span" align="left">
                <Box fontWeight="bold" color={theme.colors.accent} display="inline">
                  {!sharedPostDetails?.originalPosterCompanyName &&
                    sharedPostDetails?.originalPosterHeadline?.slice(0, 45)}
                </Box>
              </Typography>
              {!sharedPostDetails?.originalPosterCompanyName && sharedPostDetails?.originalPosterHeadline?.length > 45 && (
                <Typography variant="subtitle2" style={{ display: 'contents' }}>
                  ...
                </Typography>
              )}
              <Box>
                <Typography component="span" variant="caption">
                  <Box color={theme.colors.accent1}>
                    {moment(sharedPostDetails?.createdAt).fromNow() === moment(sharedPostDetails?.updatedAt).fromNow()
                      ? `Posted ${moment(sharedPostDetails?.createdAt).fromNow()}`
                      : `Posted ${moment(sharedPostDetails?.createdAt).fromNow()} - Last edit ${moment(
                          sharedPostDetails?.updatedAt
                        ).fromNow()}`}
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>
      <Box>
        <Box display="flex" flexDirection="column" color={theme.colors.accent1}>
          <Box p="0 0.5em">
            {sharedPostDetails?.title && (
              <StyledSharedPostTitle>
                <Typography gutterBottom variant="subtitle2" component="h2">
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: sharedPostDetails?.title
                    }}
                  />
                </Typography>
              </StyledSharedPostTitle>
            )}
            <Box display="flex" gridGap="0.5em">
              <Box marginBottom="1em">
                {!showMore && (
                  <StyledSharedPostDescription variant="body2" align="justify" ref={descriptionRefSharedPost}>
                    <ReactTextFormat allowedFormats={['URL']} linkDecorator={customLinkDecorator}>
                      {htmlToReactParser.parse(addLinks(parseContentBody(post[1]?.content), userId || ''))}
                    </ReactTextFormat>
                  </StyledSharedPostDescription>
                )}
                {showMore && (
                  <StyledSharedShowMoreDescription variant="body2" align="justify" ref={descriptionRefSharedPost}>
                    <ReactTextFormat allowedFormats={['URL']} linkDecorator={customLinkDecorator}>
                      {htmlToReactParser.parse(addLinks(parseContentBody(post[1]?.content), userId || ''))}
                    </ReactTextFormat>
                  </StyledSharedShowMoreDescription>
                )}
                {displayShowMore(post[1]?.content, descriptionRefSharedPost) && !showMore && (
                  <Link onClick={() => setShowMore(true)}>
                    <Typography component="span" variant="subtitle2">
                      <Box color={theme.colors.primary}>See More</Box>
                    </Typography>
                  </Link>
                )}
              </Box>
            </Box>
          </Box>
          <Box>
            {Array.isArray(sharedPostDetails?.media) && sharedPostDetails?.media?.length === 1 && (
              <>
                {['jpeg', 'jpg', 'png'].includes(
                  sharedPostDetails?.media[0].split('.')[sharedPostDetails?.media[0]?.split('.').length - 1]
                ) && (
                  <Box width="100%" textAlign="center" style={{ zoom: '80%' }}>
                    <PostCardImage src={sharedPostDetails?.media[0]} />
                  </Box>
                )}
                {['mov', 'mp4'].includes(
                  sharedPostDetails?.media[0]
                    .split('.')
                    [sharedPostDetails?.media[0]?.split('.').length - 1]?.toLowerCase()
                ) && (
                  <Box
                    height="auto"
                    width="100%"
                    zIndex="0"
                    // @ts-ignore
                    ref={videoRef}
                    margin={isDevice ? '0 0 3em 0' : '0'}
                  >
                    <PostCardVideo
                      url={post && sharedPostDetails?.media[0]?.length > 0 ? sharedPostDetails?.media[0] : ''}
                      id=""
                      onPlay={() => {}}
                    />
                  </Box>
                )}
              </>
            )}
            {Array.isArray(sharedPostDetails?.media) && sharedPostDetails?.media?.length > 1 && (
              <>
                {['jpeg', 'jpg', 'png'].includes(
                  sharedPostDetails?.media[0]
                    .split('.')
                    [sharedPostDetails?.media[0]?.split('.').length - 1]?.toLowerCase()
                ) && (
                  <ElasticSlider noAutoPlay>
                    {sharedPostDetails?.media?.map((mediaEl, index) => {
                      return (
                        <Box key={post?.id + '_slider' + index}>
                          <PostCardImage src={mediaEl} />
                        </Box>
                      )
                    })}
                  </ElasticSlider>
                )}
                {['mov', 'mp4'].includes(
                  sharedPostDetails?.media[0]
                    .split('.')
                    [sharedPostDetails?.media[0]?.split('.').length - 1]?.toLowerCase()
                ) && (
                  <Box height="auto" width="100%">
                    <ReactWebMediaPlayer video={sharedPostDetails?.media[0]} width="100%" color="#7E45C4" />
                  </Box>
                )}
              </>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  )
}
